/**
 * @file     SegmentedControl.tsx
 * @purpose  Hub segmented control: a recessed track (radius 16, hairline,
 *           4px inset) holding radius-12 segments, the active one on the
 *           surface with a 1px lift. A radio group with a roving tabindex:
 *           arrows move and select, Home and End jump to the ends, disabled
 *           segments are skipped. Every segment keeps a 44px hit area.
 */
'use client';

import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { hubColors, reducedMotionSx, transition } from './hubKitStyles';

export interface SegmentedOption<T extends string = string> {
  value: T;
  /** Visible text, or the accessible name when `icon` is set. */
  label: string;
  /** An icon-only segment: the icon shows and the label names it. */
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name for the group, e.g. "Bedrooms". */
  ariaLabel: string;
  /** `sm` segments are 36px, `md` 40px. Both keep a 44px hit area. */
  size?: 'sm' | 'md';
  /** Segments share the full width instead of sizing to their labels. */
  fullWidth?: boolean;
  id?: string;
  /** A visible label's id; it names the group in place of `ariaLabel`. */
  labelledBy?: string;
  describedBy?: string;
  required?: boolean;
  invalid?: boolean;
}

const INSET = 4;

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'md',
  fullWidth = false,
  id,
  labelledBy,
  describedBy,
  required,
  invalid,
}: SegmentedControlProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const selectedIndex = options.findIndex((o) => o.value === value);
  // The checked segment holds the tab stop; with none checked, the first enabled one does.
  const tabStop = selectedIndex >= 0 && !options[selectedIndex]?.disabled ? selectedIndex : (enabled[0] ?? -1);
  const segmentHeight = size === 'sm' ? 36 : 40;

  const move = (to: number) => {
    const option = options[to];
    if (!option || option.disabled) return;
    refs.current[to]?.focus();
    if (option.value !== value) onChange(option.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const position = enabled.indexOf(index);
    let target: number | undefined;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = enabled[(position + 1) % enabled.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = enabled[(position - 1 + enabled.length) % enabled.length];
        break;
      case 'Home':
        target = enabled[0];
        break;
      case 'End':
        target = enabled[enabled.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (target !== undefined) move(target);
  };

  return (
    <Box
      role="radiogroup"
      aria-label={labelledBy ? undefined : ariaLabel}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      id={id}
      sx={{
        display: fullWidth ? 'flex' : 'inline-flex',
        width: fullWidth ? '100%' : 'auto',
        maxWidth: '100%',
        gap: '2px',
        p: `${INSET - 1}px`,
        borderRadius: `${layoutTokens.radius.md}px`,
        border: `1px solid ${hubColors.borderSubtle}`,
        backgroundColor: hubColors.surfaceSunken,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={option.icon ? option.label : undefined}
            title={option.icon ? option.label : undefined}
            disabled={option.disabled}
            tabIndex={index === tabStop ? 0 : -1}
            ref={(node: HTMLButtonElement | null) => {
              refs.current[index] = node;
            }}
            onClick={() => move(index)}
            onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => onKeyDown(event, index)}
            data-checked={checked || undefined}
            sx={{
              position: 'relative',
              flex: fullWidth ? '1 1 0' : '0 0 auto',
              minWidth: 0,
              height: segmentHeight,
              px: option.icon ? 0 : size === 'sm' ? 3 : 4,
              width: option.icon ? segmentHeight : undefined,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              m: 0,
              border: 0,
              borderRadius: `${layoutTokens.radius.sm}px`,
              backgroundColor: checked ? hubColors.surfaceCard : 'transparent',
              boxShadow: checked ? `0 1px 2px ${hubColors.scrim}` : 'none',
              color: checked ? hubColors.textPrimary : hubColors.textSecondary,
              font: 'inherit',
              fontSize: size === 'sm' ? 14 : 15,
              fontWeight: checked ? fontWeightTokens.semibold : fontWeightTokens.medium,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              cursor: option.disabled ? 'not-allowed' : 'pointer',
              opacity: option.disabled ? 0.45 : 1,
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              transition: transition('background-color', 'color', 'box-shadow'),
              // The visible segment is 36 to 40px; this extends the hit area to 44.
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                height: 44,
                transform: 'translateY(-50%)',
              },
              '@media (hover: hover)': {
                '&:not([data-checked]):not(:disabled):hover': { color: hubColors.textPrimary },
              },
              '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 0 },
              ...reducedMotionSx,
            }}
          >
            {option.icon ?? option.label}
          </Box>
        );
      })}
    </Box>
  );
}
